'use client';

/**
 * Boot-order gate between the anonymous-viewer GUEST MINT and the first
 * request that needs the resulting session.
 *
 * A logged-out visitor opening `/w/<share link>` gets a guest minted
 * client-side (see ViewerGuestMint: Turnstile challenge → `/api/auth/guest`).
 * The widget iframe boots in parallel and asks for its collab token straight
 * away, so the two race — and the widget usually wins:
 *
 *     POST /api/widgets/collab/get-token  → 401   (no session yet)
 *     POST /api/auth/guest                → 200   (too late)
 *
 * The widget treats that 401 as fatal ("Could not open: permission:
 * Unauthorized") and never retries, so a perfectly valid share link dead-ends
 * on a page whose session arrived one tick later.
 *
 * This module lets the mint announce itself BEFORE it starts, so callers on
 * the critical path can wait for it (and retry once it lands) instead of
 * failing on a session that was always going to exist.
 *
 * Deliberately a module singleton, not React context: the waiter is the widget
 * bridge, which lives in a different subtree from the mint and must be able to
 * ask "is a session on its way?" without being wired to it.
 */

let pending: Promise<void> | null = null;
let settle: (() => void) | null = null;

/** Called by the mint before it starts, so waiters know a session is coming. */
export function markGuestMintPending(): void {
  if (pending) return;
  pending = new Promise<void>((resolve) => {
    settle = resolve;
  });
}

/**
 * Called when the mint finishes — success OR failure. Never leaves a waiter
 * hanging.
 *
 * Clears `pending` as well as `settle`, so a LATER mint on the same page can
 * announce itself. Before 2026-09-20 only `settle` was cleared, which left
 * `markGuestMintPending`'s `if (pending) return;` permanently short-circuited
 * after the first mint: a second announcement became a silent no-op and
 * anything on the critical path asking "is a session on its way?" got false
 * while one was in flight. Nothing needed a second mint until `host.kv`
 * started minting a visitor on an app's first SAVE
 * (`lib/auth/guest-mint-on-demand.ts`), which can run long after
 * `ViewerGuestMint` has come and gone.
 */
export function settleGuestMint(): void {
  const done = settle;
  settle = null;
  pending = null;
  done?.();
}

/** True while a mint has been announced but hasn't finished. */
export function guestMintPending(): boolean {
  return settle != null;
}

/**
 * Resolve once no mint is in flight. `true` if we actually waited for one
 * (so a caller knows a retry is worth it), `false` if there was nothing to
 * wait for. The timeout keeps a wedged/blocked mint (Turnstile never solving,
 * offline) from stalling the app open — we fall through and let the request
 * fail on its own terms.
 */
export async function awaitGuestMint(timeoutMs = 8000): Promise<boolean> {
  if (!pending || !settle) return false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  await Promise.race([
    pending,
    new Promise<void>((resolve) => {
      timer = setTimeout(resolve, timeoutMs);
    }),
  ]);
  if (timer) clearTimeout(timer);
  return true;
}
